/**
 * FeatureGate
 *
 * Route-level guard tied to a single feature key from the catalog at
 * `src/config/feature-catalog.ts`. Drops `useFeatureAccess` in front
 * of its children: when denied, either renders a fallback or
 * redirects (default: `/dashboard`).
 *
 * Why we don't just use `ProtectedRoute requirePermission="feature:X"`:
 *   - `ProtectedRoute.hasPermission` is a literal lookup against the
 *     user's permissions list. A fresh user whose role has not yet
 *     been seeded would fail every feature check and bounce.
 *   - `useFeatureAccess` carries the bootstrap fallback (catalog
 *     defaultTier ↔ role name) so pre-seed users keep working.
 *
 * Usage in App.tsx:
 *   <Route path="reviews" element={
 *     <FeatureGate featureKey="reviews">
 *       <ReviewQueuePage />
 *     </FeatureGate>
 *   } />
 */
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';

interface FeatureGateProps {
  featureKey: string;
  children: React.ReactNode;
  /** Where to redirect on deny. Defaults to /dashboard. */
  redirectTo?: string;
  /** Render this instead of redirecting on deny. */
  fallback?: React.ReactNode;
}

export function FeatureGate({
  featureKey,
  children,
  redirectTo = '/dashboard',
  fallback,
}: FeatureGateProps) {
  const { isInitialized } = useAuth();
  const allowed = useFeatureAccess(featureKey);

  // Don't make a routing decision until auth has hydrated — otherwise
  // a freshly-loaded session with `permissions` still empty would
  // bounce the user mid-render.
  if (!isInitialized) return null;
  if (allowed) return <>{children}</>;
  if (fallback !== undefined) return <>{fallback}</>;
  return <Navigate to={redirectTo} replace />;
}
